import { Dispatch, SetStateAction, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import type { ExtractKeysWithDotNotation } from "@tellescope/ui";
import { discriminatedStringUnionIncludes } from "@tellescope/utils";

/**
 * Decode a URL parameter value into its appropriate type.
 * Handles booleans, numbers, null, undefined, and strings.
 */
const decodeValue = (value: string): unknown => {
  if (value === "true") return true;
  if (value === "false") return false;
  if (value === "null") return null;
  if (value === "undefined") return undefined;

  // Try to parse as number
  const num = Number(value);
  if (!isNaN(num) && value !== "") {
    return num;
  }

  // Return as string
  return value;
};

/**
 * Set a value in a nested object using dot notation.
 * Creates intermediate objects as needed.
 */
const setNestedValue = (obj: Record<string, unknown>, path: string, value: unknown): void => {
  const keys = path.split(".");
  let current = obj;

  for (let i = 0; i < keys.length - 1; i++) {
    const key = keys[i];
    if (!key) continue;
    if (!(key in current) || typeof current[key] !== "object" || current[key] === null) {
      current[key] = {};
    }
    current = current[key] as Record<string, unknown>;
  }

  const lastKey = keys[keys.length - 1];
  if (lastKey) {
    current[lastKey] = value;
  }
};

/**
 * Parse query parameters from URLSearchParams into typed object.
 * Supports dot-notation for nested objects and array parameters.
 */
const parseQueryParams = <T extends Record<string, unknown>>(
  searchParams: URLSearchParams,
  validKeys: readonly (keyof T | string)[],
  arrayKeys: readonly (keyof T | string)[] = []
): Partial<T> => {
  const result: Record<string, unknown> = {};

  // Parse all params
  searchParams.forEach((value, key) => {
    if (!validKeys.includes(key)) return;
    // Check if this key is in arrayKeys
    if (arrayKeys.includes(key)) {
      // Get all values for this key as an array
      const allValues = searchParams.getAll(key).map(decodeValue);
      if (key.includes(".")) {
        setNestedValue(result, key, allValues);
      } else {
        result[key] = allValues;
      }
    } else if (key.includes(".")) {
      // Dot-notation key - parse into nested object
      setNestedValue(result, key, decodeValue(value));
    } else {
      // Simple key
      result[key] = decodeValue(value);
    }
  });

  return result as Partial<T>;
};

/**
 * Flatten a nested object into dot notation.
 * Arrays are preserved as values (not flattened).
 */
const flattenObject = (obj: Record<string, unknown>, prefix = ""): Record<string, unknown> => {
  const flattened: Record<string, unknown> = {};

  Object.entries(obj).forEach(([key, value]) => {
    const newKey = prefix ? `${prefix}.${key}` : key;

    if (value && typeof value === "object" && !Array.isArray(value)) {
      // Nested object - recurse
      Object.assign(flattened, flattenObject(value as Record<string, unknown>, newKey));
    } else {
      // Primitive or array - add to flattened object
      flattened[newKey] = value;
    }
  });

  return flattened;
};

/**
 * A React Router-aware hook for managing URL query parameters as React state.
 *
 * Uses React Router's useSearchParams hook, ensuring proper integration with
 * React Router navigation and location updates.
 *
 * Features:
 * - Type-safe parameter access with autocomplete
 * - Dot-notation for nested objects (e.g., dateRange.from)
 * - Automatic type coercion for booleans, numbers, and strings
 * - Array support via multiple params with same key
 * - Support for functional updates like useState
 * - Full React Router integration (works with useLocation, Link, etc.)
 *
 * @template T - The type shape of your query parameters
 * @param validKeys - Array of valid parameter keys (including dot-notation paths)
 * @param arrayKeys - Array of keys that should be parsed as arrays (optional)
 * @returns A tuple of [params, setParams] similar to useState
 *
 * @example
 * ```tsx
 * interface SearchParams {
 *   search?: string;
 *   page?: number;
 *   dateRange?: { from?: string; to?: string };
 *   tags?: string[];
 * }
 *
 * const VALID_KEYS = [
 *   "search",
 *   "page",
 *   "dateRange.from",
 *   "dateRange.to",
 *   "tags"
 * ] as const;
 *
 * const ARRAY_KEYS = ["tags"] as const;
 *
 * function SearchComponent() {
 *   const [params, setParams] = useTypedSearchParams<SearchParams>(
 *     VALID_KEYS,
 *     ARRAY_KEYS
 *   );
 *
 *   // Direct update
 *   setParams({ search: "hello", page: 1 });
 *
 *   // Nested objects use dot notation in URL
 *   setParams({ dateRange: { from: "2024-01-01", to: "2024-12-31" } });
 *   // Results in: ?dateRange.from=2024-01-01&dateRange.to=2024-12-31
 *
 *   // Arrays use multiple params
 *   setParams({ tags: ["react", "typescript"] });
 *   // Results in: ?tags=react&tags=typescript
 * }
 * ```
 */
export const useTypedSearchParams = <T extends Record<string, any>>(
  validKeys: ExtractKeysWithDotNotation<T>[],
  arrayKeys: ExtractKeysWithDotNotation<T>[] = []
): [Partial<T>, Dispatch<SetStateAction<Partial<T>>>] => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current search params into typed object
  const params = parseQueryParams<T>(searchParams, validKeys, arrayKeys);

  // Setter that supports both direct values and functional updates.
  // Uses React Router's functional updater form of setSearchParams to read
  // the latest search params, avoiding stale closure issues when multiple
  // setParams calls happen in quick succession (e.g. filter change + debounced search).
  const setParams = useCallback<Dispatch<SetStateAction<Partial<T>>>>(
    (newVal) => {
      setSearchParams(
        (prevSearchParams) => {
          // Parse the latest search params from the URL to avoid stale closures
          const currentParams = parseQueryParams<T>(prevSearchParams, validKeys, arrayKeys);

          // Support functional updates like useState
          const updatedParams = typeof newVal === "function" ? newVal(currentParams) : newVal;

          // Flatten the params object into dot-notation
          const flattenedParams = flattenObject(updatedParams);

          // Build new URLSearchParams, preserving any params not managed by this hook
          const newSearchParams = new URLSearchParams();
          prevSearchParams.forEach((value, key) => {
            if (!discriminatedStringUnionIncludes(validKeys, key)) {
              newSearchParams.append(key, value);
            }
          });

          Object.entries(flattenedParams).forEach(([key, value]) => {
            if (value === undefined || value === null) {
              return; // Skip null/undefined values
            }

            // Check if this key should be treated as an array
            if (Array.isArray(value)) {
              // Add multiple params with the same key
              value.forEach((item) => {
                newSearchParams.append(key, String(item));
              });
            } else {
              // Single value
              newSearchParams.set(key, String(value));
            }
          });

          return newSearchParams;
        },
        { replace: true }
      );
    },
    [setSearchParams, validKeys, arrayKeys]
  );

  return [params, setParams];
};
